import type { AnySchema } from 'yup';
import { boolean, ref, string } from 'yup';
import { ERROR_MESSAGES, REGEX } from '~/components/forms/Form/constants';
import config from '~/config';
import type { FormInput } from '~/types/inputs';

function getLocalisedPostcodeRegex(): RegExp {
	if (config.supportedCountries.includes('NZ')) {
		return REGEX.GLOBAL_POSTCODE;
	}

	return REGEX.AUS_POSTCODE;
}

function getLocalisedMobileRegex(): RegExp {
	if (config.supportedCountries.length > 1) {
		return REGEX.GLOBAL_MOBILE;
	}

	if (config.supportedCountries.includes('NZ')) {
		return REGEX.NZ_MOBILE_PHONE;
	}

	return REGEX.AU_MOBILE_PHONE;
}

export const getSchemaFromField = (field: FormInput): AnySchema | null | any => {
	const fieldErrorMessage = ERROR_MESSAGES[field.name] || {
		REQUIRED: 'This field is required',
		INVALID_FORMAT: 'Please enter a valid value',
	};

	if (field.name === 'date_of_birth') {
		const currentYear = new Date().getFullYear();

		return string()
			.test({
				name: 'is-valid-dob',
				test(value, ctx) {
					// If no value, skip this test and let the 'required' test handle it
					if (!value || !value.includes('-')) {
						return true;
					}

					const [year, month, day] = value.split('-');

					const dayAsNumber = parseInt(day, 10);

					if (dayAsNumber < 1 || dayAsNumber > 31) {
						return ctx.createError({
							message: 'Please enter a valid day',
						});
					}

					const monthAsNumber = parseInt(month, 10);

					if (monthAsNumber < 1 || monthAsNumber > 12) {
						return ctx.createError({
							message: 'Please enter a valid month',
						});
					}

					const yearAsNumber = parseInt(year, 10);

					if (yearAsNumber > currentYear) {
						return ctx.createError({
							message: 'Please enter a year lower than the current year',
						});
					}

					if (yearAsNumber < 1900) {
						return ctx.createError({
							message: 'Please enter a year greater than 1900',
						});
					}

					return true;
				},
			})
			.matches(/\d{4}-\d{2}-\d{2}/, 'Please enter a valid date')
			.required(fieldErrorMessage.REQUIRED);
	}

	if (field.name === 'aus_postcode') {
		return string().when('aus_postcode:manualMode', {
			is: false,
			then: string().required(fieldErrorMessage.REQUIRED),
		});
	}

	if (field.name === 'residential_postcode' || field.name === 'postcode') {
		let baseSchema = string();

		if (field.required) {
			baseSchema = baseSchema.required(fieldErrorMessage.REQUIRED);
		}

		return baseSchema
			.length(4, fieldErrorMessage.INVALID_FORMAT)
			.matches(getLocalisedPostcodeRegex(), fieldErrorMessage.INVALID_FORMAT);
	}

	if (field.name === 'confirm_mobile') {
		return string()
			.required(fieldErrorMessage.REQUIRED)
			.oneOf([ref('mobile'), null], fieldErrorMessage.INVALID_FORMAT)
			.matches(getLocalisedMobileRegex(), fieldErrorMessage.INVALID_FORMAT);
	}

	if (field.type === 'tel') {
		return string()
			.required(fieldErrorMessage.REQUIRED)
			.matches(getLocalisedMobileRegex(), fieldErrorMessage.INVALID_FORMAT);
	}

	if (field.name === 'email') {
		return string()
			.email(fieldErrorMessage.INVALID_FORMAT)
			.required(fieldErrorMessage.REQUIRED);
	}

	if (field.name === 'confirm_email') {
		return string()
			.oneOf([ref('email'), null], fieldErrorMessage.INVALID_FORMAT)
			.required(fieldErrorMessage.REQUIRED);
	}

	if (!field.required) {
		return null;
	}

	if (field.type === 'checkbox') {
		return boolean()
			.required(fieldErrorMessage.REQUIRED)
			.oneOf([true], fieldErrorMessage.REQUIRED);
	}

	return string().required(fieldErrorMessage.REQUIRED);
};
